import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, FileDown, Send, RefreshCw, AlertTriangle, CheckCircle2, Activity, Eye, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { apiRequest, downloadFile, API_BASE } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export default function Newsletter() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiRequest(`/companies/${id}`).then(setCompany).catch((e) => setError(e.message));
  }, [id]);

  const loadPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("dvr_token");
      const r = await fetch(`${API_BASE}/companies/${id}/newsletter/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setPreviewHtml(await r.text());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTimeline = async () => {
    setTimelineLoading(true);
    try {
      const r = await apiRequest(`/companies/${id}/newsletter/timeline?limit=100`);
      setTimeline(r);
    } catch (err) {
      toast.error("Errore caricamento timeline", { description: err.message });
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const downloadPdf = async () => {
    try {
      await downloadFile(`/companies/${id}/newsletter/pdf`, "newsletter.pdf");
      toast.success("PDF newsletter scaricato");
    } catch (err) {
      toast.error(`Errore download: ${err.message}`);
    }
  };

  const sendNewsletter = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await apiRequest(`/companies/${id}/newsletter/send`, { method: "POST" });
      setLastResult(r);
      if (r.email?.sent) {
        toast.success(`Newsletter inviata a ${r.email.recipients.join(", ")}`);
      } else if (r.email?.reason === "smtp_non_configurato") {
        toast.warning("Newsletter generata e archiviata. SMTP non configurato: nessun invio email.");
      } else if (r.email?.reason === "nessun_destinatario") {
        toast.warning("Newsletter archiviata ma nessun destinatario email trovato.");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="outline" data-testid="newsletter-back-button">
          <Link to={`/aziende/${id}`}>
            <ArrowLeft className="h-4 w-4" /> Scheda azienda
          </Link>
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPreview} disabled={loading} data-testid="newsletter-refresh-button">
            <RefreshCw className="h-4 w-4" /> Rigenera anteprima
          </Button>
          <Button variant="outline" onClick={downloadPdf} disabled={loading} data-testid="newsletter-pdf-button">
            <FileDown className="h-4 w-4" /> Scarica PDF
          </Button>
          <Button
            className="bg-blue-900 hover:bg-blue-800"
            onClick={sendNewsletter}
            disabled={loading}
            data-testid="newsletter-send-button"
          >
            <Send className="h-4 w-4" /> Genera e invia
          </Button>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
          <Mail className="mr-1 inline h-3 w-3" /> Newsletter mensile
        </p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="newsletter-title">
          {company?.business_name || "Azienda"}
        </h2>
        <p className="mt-2 text-slate-600">
          Newsletter automatica con KPI di sicurezza, top-3 azioni AI suggerite,
          scadenze imminenti e stato revisioni DVR. Inviata via PEC/SMTP al
          Datore di Lavoro (cron mensile: 1° di ogni mese alle 07:30).
        </p>
      </section>

      {error && (
        <Alert variant="destructive" data-testid="newsletter-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {lastResult && (
        <Alert
          className={
            lastResult.email?.sent
              ? "border-emerald-200 bg-emerald-50"
              : "border-amber-200 bg-amber-50"
          }
          data-testid="newsletter-last-result"
        >
          {lastResult.email?.sent ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-700" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-700" />
          )}
          <AlertTitle>
            {lastResult.email?.sent ? "Inviata" : "Archiviata"} ·{" "}
            {lastResult.period}
          </AlertTitle>
          <AlertDescription>
            Documento ID: <code>{lastResult.document_id?.slice(0, 8)}…</code>
            {lastResult.email?.recipients?.length > 0 && (
              <div className="mt-1">
                Destinatari:{" "}
                {lastResult.email.recipients.map((r) => (
                  <Badge key={r} className="mr-1 bg-white text-slate-700">
                    {r}
                  </Badge>
                ))}
              </div>
            )}
            {!lastResult.email?.sent && lastResult.email?.reason && (
              <div className="mt-1 text-xs">
                Motivo no-send: <code>{lastResult.email.reason}</code>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card className="shadow-none" data-testid="newsletter-timeline-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2"><Activity className="h-4 w-4 text-blue-900" /> Timeline eventi newsletter</CardTitle>
            <Button variant="outline" size="sm" onClick={loadTimeline} disabled={timelineLoading} data-testid="newsletter-timeline-refresh-button">
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!timeline || timelineLoading ? (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-slate-500" data-testid="newsletter-timeline-loading">Caricamento timeline…</p>
          ) : timeline.events_total === 0 ? (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-slate-500" data-testid="newsletter-timeline-empty">
              Nessun evento registrato. Gli eventi (aperture e click) verranno tracciati automaticamente quando i destinatari interagiscono con la newsletter inviata via email.
            </p>
          ) : (
            <ol className="relative space-y-3 border-l-2 border-slate-200 pl-5" data-testid="newsletter-timeline-list">
              {timeline.events.map((ev) => {
                const isOpen = ev.event_type === "open";
                return (
                  <li key={ev.id} className="relative" data-testid={`newsletter-timeline-event-${ev.id}`}>
                    <span className={`absolute -left-[26px] top-1 flex h-4 w-4 items-center justify-center rounded-full ${isOpen ? "bg-emerald-500" : "bg-blue-500"}`}>
                      {isOpen ? <Eye className="h-2.5 w-2.5 text-white" /> : <MousePointerClick className="h-2.5 w-2.5 text-white" />}
                    </span>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-950">{ev.document_name || "Newsletter"}</p>
                        <Badge className={isOpen ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-900"} data-testid={`newsletter-timeline-type-${ev.id}`}>
                          {isOpen ? "Apertura" : "Click"}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">{(ev.created_at || "").slice(0, 19).replace("T", " ")}</p>
                      {!isOpen && ev.target && (
                        <p className="mt-1 break-all text-xs text-slate-500">→ <span className="font-mono">{ev.target}</span>{ev.utm ? ` · utm=${ev.utm}` : ""}</p>
                      )}
                      {isOpen && ev.user_agent && (
                        <p className="mt-1 truncate text-xs text-slate-400">{ev.user_agent}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Anteprima HTML</CardTitle>
        </CardHeader>
        <CardContent>
          {previewHtml ? (
            <iframe
              data-testid="newsletter-preview-iframe"
              title="Anteprima newsletter"
              srcDoc={previewHtml}
              style={{ width: "100%", minHeight: "70vh", border: "1px solid #e2e8f0", borderRadius: 6 }}
            />
          ) : (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-slate-500" data-testid="newsletter-preview-empty">
              Caricamento anteprima…
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
